"use client";

import { useCallback, useEffect, useState } from "react";
import { fetchApi } from "@/lib/api-fetch";
import { apiFetchPath } from "@/lib/api-fetch-path";
import { OPEN_COOKIE_PREFERENCES_EVENT } from "@/components/cookie-preferences-trigger";

export const STORAGE_KEY = "mot-cookie-preferences-v1";

/** Dispatched when cookie prefs are loaded or saved — analytics listeners (Plausible) use this. */
export const MOT_COOKIE_PREFERENCES_CHANGED = "mot-cookie-preferences-changed";

export interface CookiePreferences {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
}

function loadStored(): CookiePreferences | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
    if (parsed.essential !== true || typeof parsed.analytics !== "boolean" || typeof parsed.marketing !== "boolean") {
      return null;
    }
    return {
      essential: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

async function postConsent(prefs: Pick<CookiePreferences, "essential" | "analytics" | "marketing">) {
  try {
    const res = await fetchApi(apiFetchPath("/api/v1/compliance/cookie-consent"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(prefs),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      console.warn("cookie-consent POST failed", { status: res.status, detail: detail.slice(0, 200) });
    }
  } catch (e) {
    console.warn("cookie-consent POST error", e);
  }
}

export function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const applyPreferences = useCallback(
    async (next: { analytics: boolean; marketing: boolean }) => {
      const prefs: CookiePreferences = {
        essential: true,
        analytics: next.analytics,
        marketing: next.marketing,
        updatedAt: new Date().toISOString(),
      };
      try {
        if (typeof window !== "undefined" && "localStorage" in window) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        }
      } catch {
        // Quota or restricted storage: keep in-memory state only
      }
      setAnalytics(next.analytics);
      setMarketing(next.marketing);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(MOT_COOKIE_PREFERENCES_CHANGED, {
            detail: { analytics: next.analytics, marketing: next.marketing },
          }),
        );
      }
      await postConsent({ essential: true, analytics: next.analytics, marketing: next.marketing });
      setOpen(false);
    },
    [],
  );

  useEffect(() => {
    setMounted(true);
    const existing = loadStored();
    if (existing) {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
      window.dispatchEvent(
        new CustomEvent(MOT_COOKIE_PREFERENCES_CHANGED, {
          detail: { analytics: existing.analytics, marketing: existing.marketing },
        }),
      );
    } else {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    function onOpen() {
      const existing = loadStored();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
        window.dispatchEvent(
          new CustomEvent(MOT_COOKIE_PREFERENCES_CHANGED, {
            detail: { analytics: existing.analytics, marketing: existing.marketing },
          }),
        );
      }
      setOpen(true);
    }
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, onOpen);
  }, []);

  if (!mounted || !open) {
    return null;
  }

  return (
    <div
      id="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-dark-border bg-dark-card/95 p-4 shadow-[0_-8px_32px_rgba(0,0,0,0.45)] backdrop-blur-md sm:p-6"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h2 id="cookie-consent-title" className="text-sm font-bold text-mot-white sm:text-base">
            Cookies & privacy choices
          </h2>
          <p className="mt-2 text-xs leading-relaxed text-mot-grey-light sm:text-sm">
            We use essential cookies to run the site. Optional analytics and marketing cookies help us improve and
            promote MOT Cards. You can change your mind anytime from Privacy Policy → Cookie preferences.
          </p>
          <ul className="mt-4 space-y-3 text-sm text-mot-grey-light">
            <li className="flex items-start gap-3">
              <input id="cookie-essential" type="checkbox" checked disabled className="mt-1 h-4 w-4 rounded border-dark-border" />
              <label htmlFor="cookie-essential" className="cursor-not-allowed opacity-90">
                <span className="font-medium text-mot-white">Essential</span>
                <span className="mt-0.5 block text-xs text-mot-grey-light">Required for security, session, and checkout.</span>
              </label>
            </li>
            <li className="flex items-start gap-3">
              <input
                id="cookie-analytics"
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-dark-border accent-mot-teal"
              />
              <label htmlFor="cookie-analytics" className="cursor-pointer">
                <span className="font-medium text-mot-white">Analytics</span>
                <span className="mt-0.5 block text-xs text-mot-grey-light">Helps us understand product usage (only if enabled).</span>
              </label>
            </li>
            <li className="flex items-start gap-3">
              <input
                id="cookie-marketing"
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-dark-border accent-mot-teal"
              />
              <label htmlFor="cookie-marketing" className="cursor-pointer">
                <span className="font-medium text-mot-white">Marketing</span>
                <span className="mt-0.5 block text-xs text-mot-grey-light">Personalization and campaign measurement (only if enabled).</span>
              </label>
            </li>
          </ul>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:w-48">
          <button
            type="button"
            className="rounded-lg bg-mot-teal px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-mot-teal/20 transition-colors hover:bg-mot-teal-light"
            onClick={() => applyPreferences({ analytics: true, marketing: true })}
          >
            Accept all
          </button>
          <button
            type="button"
            className="rounded-lg border border-dark-border px-4 py-2.5 text-sm font-medium text-mot-grey-light transition-colors hover:border-mot-teal/30 hover:text-mot-teal"
            onClick={() => applyPreferences({ analytics: false, marketing: false })}
          >
            Reject non-essential
          </button>
          <button
            type="button"
            className="rounded-lg border border-dark-border bg-mot-black/40 px-4 py-2.5 text-sm font-medium text-mot-white transition-colors hover:border-mot-teal/30"
            onClick={() => applyPreferences({ analytics, marketing })}
          >
            Save choices
          </button>
        </div>
      </div>
    </div>
  );
}
